import React from 'react';
import { Box, Typography, Button, Container, Chip, Stack } from '@mui/material';
import { styled, keyframes } from '@mui/system';

import AppShortcutIcon from '@mui/icons-material/AppShortcut';
import SettingsIcon from '@mui/icons-material/Settings';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';

/* ================= Animaciones ================= */
const fadeInUp = keyframes`
  from { opacity: 0; transform: translate3d(0, 24px, 0); }
  to   { opacity: 1; transform: translate3d(0, 0, 0); }
`;

/* ================ Layout base (nuevo diseño) ================ */
const HeroWrap = styled('section')({
  position: 'relative',
  overflow: 'hidden',
  padding: '96px 0 120px',
  minHeight: '76vh',
  background:
    'linear-gradient(180deg, #0E0F13 0%, #11131A 55%, #12141C 100%)',
  backgroundImage:
    'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(180deg, #0E0F13 0%, #11131A 55%, #12141C 100%)',
  backgroundSize: '14px 14px, 100% 100%',
});

/* Franja lateral dorada (accent) */
const SideAccent = styled('div')({
  position: 'absolute',
  right: 0,
  top: 0,
  height: '100%',
  width: 'min(34vw, 560px)',
  background:
    'linear-gradient(180deg, rgba(218,165,32,.18) 0%, rgba(218,165,32,.06) 60%, rgba(218,165,32,0) 100%)',
  boxShadow: '-40px 0 120px rgba(218,165,32,.10) inset',
  backdropFilter: 'blur(2px)',
  maskImage:
    'linear-gradient(90deg, transparent 0%, black 25%, black 100%)',
  pointerEvents: 'none',
});

/* Contenedor del contenido con borde superior curvo */
const Panel = styled('div')(({ theme }) => ({
  position: 'relative',
  width: 'min(100%, 1200px)',
  marginInline: 'auto',
  borderRadius: 22,
  padding: '40px 44px',
  background:
    'linear-gradient(180deg, rgba(255,255,255,.05) 0%, rgba(255,255,255,.02) 100%)',
  border: '1px solid rgba(255,255,255,.09)',
  boxShadow: '0 20px 60px rgba(0,0,0,.45)',
  animation: `${fadeInUp} .6s ease .1s both`,
  [theme.breakpoints.up('md')]: {
    padding: '48px 56px',
  },
}));

/* Grid asimétrico: contenido + tarjeta */
const GridWrap = styled('div')(({ theme }) => ({
  display: 'grid',
  gap: 28,
  gridTemplateColumns: '1fr',
  alignItems: 'center',
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: '1.35fr .9fr',
  },
}));

/* ================ Tipografías ================ */
const Eyebrow = styled(Typography)({
  color: '#E8E8EA',
  fontWeight: 900,
  letterSpacing: '.18em',
  textTransform: 'uppercase',
  fontSize: 12,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 10px',
  borderRadius: 999,
  background:
    'linear-gradient(180deg, rgba(218,165,32,.14) 0%, rgba(218,165,32,.06) 100%)',
  border: '1px solid rgba(218,165,32,.35)',
  marginBottom: 16,
});

const Title = styled(Typography)({
  fontSize: 'clamp(36px, 6vw, 64px)',
  lineHeight: 1.03,
  letterSpacing: '-.02em',
  fontWeight: 900,
  color: '#FFFFFF',
  marginBottom: 10,
  textWrap: 'balance',
});

const SubTitle = styled(Typography)({
  fontSize: 'clamp(18px, 2vw, 22px)',
  color: 'rgba(255,255,255,.86)',
  marginBottom: 22,
});

/* Línea de features */
const Line = styled(Typography)({
  color: 'rgba(255,255,255,.92)',
  lineHeight: 1.9,
  marginBottom: 10,
  fontSize: 16,
  letterSpacing: '.01em',
});

const PillIcon = {
  color: '#DAA520',
  border: '2px solid rgba(0,0,0,.85)',
  background: 'rgba(0,0,0,.85)',
  borderRadius: '50%',
  boxShadow: '0 6px 16px rgba(0,0,0,.35)',
  mr: 1,
  p: '2px',
  verticalAlign: 'middle',
  transform: 'translateY(-1px)',
};

/* ================ CTAs ================ */
const Primary = styled(Button)({
  position: 'relative',
  fontSize: 16,
  fontWeight: 900,
  letterSpacing: '.08em',
  padding: '16px 26px',
  borderRadius: 12,
  textTransform: 'uppercase',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 10,
  color: '#0B0B0B',
  background: 'linear-gradient(180deg, #F3D462 0%, #DAA520 100%)',
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,.35), 0 14px 28px rgba(218,165,32,.30)',
  transition: 'transform .16s ease, box-shadow .25s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 18px 40px rgba(0,0,0,.28)',
  },
});

const Secondary = styled(Button)({
  fontSize: 15,
  fontWeight: 800,
  padding: '14px 22px',
  borderRadius: 12,
  textTransform: 'uppercase',
  color: '#E8E8EA',
  border: '1px solid rgba(255,255,255,.18)',
  background: 'rgba(255,255,255,.04)',
  '&:hover': {
    background: 'rgba(255,255,255,.08)',
  },
});

/* ================ Tarjeta lateral destacada ================ */
const SideCard = styled('div')({
  position: 'relative',
  borderRadius: 18,
  padding: 22,
  background:
    'linear-gradient(160deg, rgba(218,165,32,.14) 0%, rgba(255,255,255,.05) 100%)',
  border: '1px solid rgba(218,165,32,.28)',
  boxShadow: '0 16px 46px rgba(0,0,0,.38)',
  minHeight: 240,
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
});

const Seal = styled('div')({
  position: 'absolute',
  top: -14,
  right: -14,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 10px',
  fontWeight: 900,
  fontSize: 12,
  letterSpacing: '.06em',
  textTransform: 'uppercase',
  color: '#F5DEB3',
  background: 'rgba(0,0,0,.75)',
  border: '1px solid rgba(218,165,32,.4)',
  borderRadius: 999,
  boxShadow: '0 8px 22px rgba(0,0,0,.35)',
});

/* Lista de puntos fuertes en la tarjeta */
const Bullet = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  color: 'rgba(255,255,255,.92)',
  fontWeight: 600,
  fontSize: 14,
});

/* ================= Componente ================= */
const CreativeHeroSection = () => {
  return (
    <HeroWrap>
      <SideAccent />
      <Container maxWidth="lg">
        <Panel>
          <GridWrap>
            {/* Columna de texto */}
            <Box>
              <Eyebrow component="span">VENTA POR MENOR Y MAYOR</Eyebrow>

              <Title variant="h1" sx={{ textAlign: 'center', mx: 'auto' }}>
  Persianas de aluminio inyectado con poliuretano y de PVC
</Title>

<SubTitle sx={{ textAlign: 'center', mx: 'auto' }}>
  Calidad que se ve, confort que se siente.
</SubTitle>

              <Line>
                <SupportAgentIcon sx={PillIcon} />
                Ofrecemos <b>calidad, rápida entrega y buen asesoramiento</b>.
              </Line>
              <Line>
                <SettingsIcon sx={PillIcon} />
                Lamas de aluminio inyectadas con <b>aislamiento al frío, calor y ruidos</b>.
              </Line>
              <Line sx={{ mb: 3 }}>
                <FamilyRestroomIcon sx={PillIcon} />
                <b>Empresa familiar</b> con más de 30 años de experiencia.
              </Line>

              <Stack direction="row" gap={1.5} flexWrap="wrap">
                <Primary
                  href="https://walink.co/5a9e53"
                  aria-label="Comunícate por WhatsApp"
                  rel="noopener noreferrer"
                >
                  COMUNICATE <AppShortcutIcon fontSize="small" />
                </Primary>
               
              </Stack>
            </Box>

            {/* Tarjeta lateral */}
            <SideCard>
              <Seal><WorkspacePremiumIcon fontSize="small" /> 30+ Años</Seal>

              <Typography variant="subtitle1" sx={{ color: '#FFF', fontWeight: 800 }}>
                ¿Por qué elegirnos?
              </Typography>

              <Bullet>
                <CheckRoundedIcon sx={{ color: '#DAA520' }} /> Materiales premium y terminaciones limpias
              </Bullet>
              <Bullet>
                <CheckRoundedIcon sx={{ color: '#DAA520' }} /> Asesoramiento técnico para tu necesidad
              </Bullet>
              <Bullet>
                <CheckRoundedIcon sx={{ color: '#DAA520' }} /> Entrega rápida y soporte post-venta
              </Bullet>

              <Stack direction="row" spacing={1} sx={{ mt: 'auto' }}>
                <Chip
                  label="Garantía"
                  size="small"
                  sx={{
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,.18)',
                    background: 'rgba(255,255,255,.05)',
                    borderRadius: 10,
                    fontWeight: 700,
                  }}
                />
                <Chip
                  label="Instalación"
                  size="small"
                  sx={{
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,.18)',
                    background: 'rgba(255,255,255,.05)',
                    borderRadius: 10,
                    fontWeight: 700,
                  }}
                />
              </Stack>
            </SideCard>
          </GridWrap>
        </Panel>
      </Container>
    </HeroWrap>
  );
};

export default CreativeHeroSection;
